import React, { useState, useEffect, useCallback } from 'react'
import { Box, Button, Typography, Fade, IconButton, Alert, Snackbar } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { answerNPS, unanswerNPS } from '@/services/user.service'
import useAuth from '@/context/useAuth'
import { useTranslation } from 'react-i18next'

interface NPSFeedbackProps {
    // You can pass props if needed, but for now we'll manage everything internally
}

interface NPSData {
    title: string
    description: string
    minLabel?: string
    maxLabel?: string
    npsId: number
}

const NPSFeedback: React.FC<NPSFeedbackProps> = () => {
    const { me } = useAuth()
    const { t } = useTranslation()
    const [isVisible, setIsVisible] = useState(false)
    const [hasAnswered, setHasAnswered] = useState(false)
    const [selectedScore, setSelectedScore] = useState<number | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [npsData, setNpsData] = useState<NPSData | null>(null)
    const [alertOpen, setAlertOpen] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')
    const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success')

    // Check if user has already answered and fetch NPS data
    const checkAndLoadSurvey = useCallback(async () => {
        try {
            const response = await unanswerNPS()

            if (response?.nps) {
                // Check if NPS is agrid_only and verify user email
                if (response.nps.Status === 'agrid_only') {
                    const userEmail = me?.email || ''
                    if (!userEmail.includes('@a-grid.com')) {
                        // User is not from A-Grid, don't show NPS
                        setHasAnswered(true)
                        return
                    }
                }

                // Check if user has dismissed this NPS within the last 30 days
                const dismissedKey = `nps_dismissed_${response.nps.NpsId}`
                const dismissedTimestamp = localStorage.getItem(dismissedKey)
                if (dismissedTimestamp) {
                    const dismissedDate = new Date(dismissedTimestamp)
                    const now = new Date()
                    const thirtyDaysInMs = 60 * 24 * 60 * 60 * 1000 // 30 days
                    
                    if (now.getTime() - dismissedDate.getTime() < thirtyDaysInMs) {
                        // Less than 30 days have passed, don't show NPS
                        setHasAnswered(true)
                        return
                    }
                    // More than 30 days have passed, remove the dismissed flag
                    localStorage.removeItem(dismissedKey)
                }

                // User hasn't answered, show the NPS
                setNpsData({
                    title: response.nps.Title || 'Donnez-nous votre avis en un clic !',
                    description: response.nps.Description || 'Êtes-vous satisfait de la plateforme ?',
                    npsId: response.nps.NpsId,
                })
                // Show the component after a short delay for better UX
                setTimeout(() => setIsVisible(true), 1000)
            } else {
                // User has already answered
                setHasAnswered(true)
            }
        } catch (error) {
            // Error checking NPS status or user has already answered
            setHasAnswered(true)
        }
    }, [me])

    useEffect(() => {
        checkAndLoadSurvey()
    }, [checkAndLoadSurvey])

    // Handle score selection
    const handleScoreClick = async (score: number) => {
        if (!npsData?.npsId) return

        setSelectedScore(score)
        setIsSubmitting(true)

        try {
            const response = await answerNPS({ npsId: npsData.npsId, score })

            if (response?.success) {
                // Success - show success message
                setAlertSeverity('success')
                setAlertMessage(t('nps.success'))
                setAlertOpen(true)

                // After successful submission
                setTimeout(() => {
                    setIsVisible(false)
                    setHasAnswered(true)
                }, 500)
            } else {
                // Error - show error message based on errorMsg
                setAlertSeverity('error')
                const errorKey = response?.errorMsg || 'generic_error'
                setAlertMessage(t(`nps.${errorKey}`, { defaultValue: t('nps.error') }))
                setAlertOpen(true)
                setIsSubmitting(false)
                setSelectedScore(null)
            }
        } catch (error) {
            // Network or other error
            setAlertSeverity('error')
            setAlertMessage(t('nps.generic_error'))
            setAlertOpen(true)
            setIsSubmitting(false)
            setSelectedScore(null)
        }
    }

    // Handle alert close
    const handleAlertClose = () => {
        setAlertOpen(false)
    }

    // Handle close button
    const handleClose = () => {
        if (npsData?.npsId) {
            // Store dismiss timestamp with NPS ID to track per survey
            const dismissedKey = `nps_dismissed_${npsData.npsId}`
            localStorage.setItem(dismissedKey, new Date().toISOString())
        }
        setIsVisible(false)
    }

    // // Don't render if user has already answered or no data
    // if (hasAnswered || !npsData) {
    //     return null
    // }

    return (
        <>
            <Snackbar
                open={alertOpen}
                autoHideDuration={6000}
                onClose={handleAlertClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: '100%' }}>
                    {alertMessage}
                </Alert>
            </Snackbar>
            {!(hasAnswered || !npsData) && (
                <>
                    <Fade in={isVisible} timeout={500}>
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 80,
                                right: 20,
                                zIndex: 9999,
                                maxWidth: 420,
                                width: 'calc(100% - 40px)',
                                '@media (max-width: 768px)': {
                                    top: 70,
                                    right: 10,
                                    left: 10,
                                    width: 'calc(100% - 20px)',
                                    maxWidth: 'none',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    background: 'linear-gradient(135deg, #4a7ba7 0%, #2d5a7b 100%)',
                                    borderRadius: 3,
                                    padding: 2.5,
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                                    position: 'relative',
                                    '@media (max-width: 768px)': {
                                        padding: 2,
                                    },
                                }}
                            >
                                <IconButton
                                    onClick={handleClose}
                                    size="small"
                                    disabled={isSubmitting}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        '&:hover': {
                                            color: 'rgba(255, 255, 255, 1)',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        },
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>

                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        marginBottom: 1,
                                        textAlign: 'center',
                                        paddingRight: 3,
                                    }}
                                >
                                    {npsData.title}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'white',
                                        fontSize: '14px',
                                        marginBottom: 2,
                                        textAlign: 'center',
                                        fontWeight: 500,
                                    }}
                                >
                                    {npsData.description}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 0.75,
                                        justifyContent: 'center',
                                        flexWrap: 'wrap',
                                        marginBottom: 1,
                                    }}
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                                        <Button
                                            key={score}
                                            onClick={() => handleScoreClick(score)}
                                            disabled={isSubmitting}
                                            variant={selectedScore === score ? 'contained' : 'outlined'}
                                            sx={{
                                                minWidth: 30,
                                                height: 30,
                                                borderRadius: 1.5,
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: selectedScore === score ? '#2d5a7b' : 'white',
                                                border: '2px solid rgba(255, 255, 255, 0.5)',
                                                backgroundColor:
                                                    selectedScore === score
                                                        ? 'rgba(255, 255, 255, 0.95)'
                                                        : 'rgba(255, 255, 255, 0.1)',
                                                transition: 'all 0.3s ease',
                                                padding: 0,
                                                opacity: isSubmitting ? 0.6 : 1,
                                                '&:hover:not(:disabled)': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                                    borderColor: 'rgba(255, 255, 255, 0.9)',
                                                    transform: 'translateY(-2px)',
                                                },
                                                '&.Mui-disabled': {
                                                    color: 'white',
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                },
                                                ...(selectedScore === score && {
                                                    transform: 'scale(1.1)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                    },
                                                }),
                                                '@media (max-width: 480px)': {
                                                    minWidth: 32,
                                                    height: 32,
                                                    fontSize: '13px',
                                                },
                                            }}
                                        >
                                            {score}
                                        </Button>
                                    ))}
                                </Box>

                                {npsData.minLabel && npsData.maxLabel && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginTop: 1,
                                            paddingX: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.8)',
                                                fontSize: '11px',
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            {npsData.minLabel}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.8)',
                                                fontSize: '11px',
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            {npsData.maxLabel}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Fade>
                </>
            )}
        </>
    )
}

export default NPSFeedback
